import { Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { BoundTextEditor } from "minimals-template/components/editor"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { hasOverride } from "library/extend-schedule.mjs"
import { isShareLinkContentOverrideEnabled } from "routes/schedule/plugins/share-link-comments/share-link-content-overrides-feature-flag"

// Inline override editor — bound to the regime payload, not the master record. Rendered inside
// <Payload>/<PayloadTask>, so `target` is the payload we write to and `payloadTarget` is the master we
// seed from. We use BoundTextEditor (the always-on Jodit editor that "Add Introduction" uses) rather
// than BoundEditableRichText, because the tailoring surface doesn't set `editMode`, so the
// click-to-edit (Editing) variant would render read-only here. `defaultValue` shows the master text
// until the user types (display-only, never persisted); clearing the field empties the override and
// falls back to master at render time (see extend-schedule.mjs).
//
// `singleLine` swaps the Jodit body editor for a compact always-on text field — used for the task title,
// which is a short heading rather than a rich-text body, so a full block editor would not fit its row.
export function ContentOverrideField({ field, sourcePath, singleLine = false }) {
    const { payloadTarget, target } = useBoundContext()
    const master = Object.get(payloadTarget, sourcePath) ?? ""
    // Overrides persist and keep rendering after the rollout flag is turned off; the flag only
    // controls editability (FIQ-1631). Without the flag, show the effective value read-only instead
    // of an editor: the stored override, or the master text when no override is set (the gates in
    // content-override-gates.js only mount this flag-off for the intro when either intro field has
    // an override, so the sibling field may legitimately have none).
    if (!isShareLinkContentOverrideEnabled()) {
        const override = Object.get(target ?? {}, field)
        const value = hasOverride(override) ? override : master
        if (singleLine) {
            return <span data-cy={`content-override-display-${field}`}>{value}</span>
        }
        return (
            <Typography
                component="div"
                data-cy={`content-override-display-${field}`}
                dangerouslySetInnerHTML={{ __html: value }}
            />
        )
    }
    if (singleLine) {
        // BoundTextField (not BoundDebouncedTextField) writes the payload target on every keystroke, so a
        // quick edit-then-blur/publish is never dropped in a field-level debounce — the regime payload's
        // own useOfflineSave({ wait: 1000 }) still throttles persistence. fontSize is pinned so the input
        // does not inherit the surrounding task-title h2 typography (MUI inputs use `font: inherit`).
        return (
            <BoundTextField
                field={field}
                defaultValue={master}
                label=""
                size="small"
                variant="standard"
                sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem", py: 0.25 } }}
            />
        )
    }
    // No label — BoundTextEditor would otherwise humanise the field name into a floating label
    // ("Intro Content Override"), which is noise and shifts the editor down.
    return <BoundTextEditor field={field} defaultValue={master} label="" />
}
